/**
 * -------------------------------------------------------------------------------------------------
 * GLOBO PRODUCT OPTIONS -> LIGNES D'ADD-ON ORPHELINES
 * -------------------------------------------------------------------------------------------------
 *
 * Depuis que la broderie est facturée par un vrai produit (mode « Use existing product » côté app),
 * elle occupe sa propre ligne de panier. Globo relie les deux lignes par une paire de propriétés :
 * l'article brodé porte `_gpo_product_group`, la ligne de broderie `_gpo_parent_product_group` avec
 * la même valeur.
 *
 * L'app est censée retirer l'add-on quand son article parent quitte le panier. Sa réconciliation ne
 * reconnaît pas Stretch (comme son réglage « masquer la quantité et le bouton supprimer », resté
 * sans effet) : supprimer le peignoir laisse la broderie seule, à 15 €. Et puisque le thème masque
 * désormais les contrôles sur cette ligne — y laisser un bouton permettrait de retirer la broderie
 * en gardant sa demande sur le produit, donc une commande infulfillable — le client n'aurait plus
 * aucun moyen de s'en défaire.
 *
 * Ce fichier ferme la boucle : après chaque changement de panier, toute ligne d'add-on dont le
 * parent a disparu est retirée.
 *
 * À vérifier à chaque mise à jour de l'app : les deux noms de propriétés ne sont pas documentés.
 */

(() => {
  const root = window.Shopify?.routes?.root ?? '/';

  /* Le nettoyage modifie le panier, ce qui émet un nouvel événement : sans ce drapeau, chaque
     passage relancerait le suivant. Une fois les orphelines retirées il n'en reste aucune, donc la
     deuxième passe ne fait rien — le drapeau protège surtout des appels concurrents. */
  let running = false;

  const removeOrphanAddons = async () => {
    if (running) return false;
    running = true;

    try {
      const cart = await (await fetch(`${root}cart.js`)).json();

      const parentGroups = new Set(
        cart.items.map((item) => item.properties?._gpo_product_group).filter(Boolean)
      );

      const orphans = cart.items.filter((item) => {
        const group = item.properties?._gpo_parent_product_group;
        return group && !parentGroups.has(group);
      });

      if (orphans.length === 0) return false;

      /* Séquentiel, pas en parallèle : deux écritures simultanées sur le panier se répondent avec
         des états divergents, et la seconde peut réintroduire la ligne que la première a retirée. */
      for (const orphan of orphans) {
        await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: orphan.key, quantity: 0 }),
        });
      }

      return true;
    } catch {
      /* Un panier qu'on n'a pas su lire se répare au prochain événement : mieux vaut ne rien faire
         que de risquer de supprimer une ligne sur un état partiel. */
      return false;
    } finally {
      running = false;
    }
  };

  /* Le tiroir se réinjecte à partir de la section, il suffit donc de lui demander un rafraîchissement.
     La page panier, elle, supprime par un lien : la navigation a déjà eu lieu et le tableau rendu
     contient encore l'orpheline, d'où le rechargement — il n'arrive qu'une fois, puisqu'au retour il
     n'y a plus rien à nettoyer. */
  const refresh = () => {
    if (window.location.pathname.endsWith('/cart')) {
      window.location.reload();
    } else {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
  };

  const reconcile = async () => {
    if (await removeOrphanAddons()) refresh();
  };

  document.documentElement.addEventListener('cart:change', reconcile);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile, { once: true });
  } else {
    reconcile();
  }
})();
